import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { FaChevronLeft, FaShoppingCart, FaTimes } from "react-icons/fa";
import { listProductDetails } from "../actions/productActions";
import { addToCart } from "../actions/cartActions";
import ProductDetailSkeleton from "../components/ProductDetailSkeleton";
import Message from "../components/Message";
import ProductImage from "../components/ProductImage";
import UpsellModule from "../components/UpsellModule";
import { t, ALLERGEN_LABELS } from "../i18n/translations";
import "./ProductDetailScreen.css";

// Dragging the sheet's handle past this many px triggers a close instead
// of snapping back — matches the "flick it away" feel of Wolt's own sheet.
const SWIPE_CLOSE_THRESHOLD = 80;

function ProductDetailScreen() {
  const { id } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  // Opened in-app (Product.js passes state.backgroundLocation so the menu
  // list keeps rendering behind it — see App.js's AppRoutes) → render as a
  // Wolt-style sheet over the dimmed menu. A direct/shared link to this
  // URL has no background to overlay, so it falls back to a plain page.
  const isOverlay = Boolean(location.state?.backgroundLocation);

  const [qty, setQuantity] = useState(1);
  const [selectedOptions, setSelectedOptions] = useState({});
  const [showErrors, setShowErrors] = useState(false);

  const productDetails = useSelector((state) => state.productDetails);
  const { loading, error, product } = productDetails;

  const { language } = useSelector((state) => state.language);
  const { restaurant = {} } = useSelector((state) => state.restaurantDetails || {});
  const isClosed = restaurant.is_open_now === false;
  const hidePrices = restaurant.hide_prices === true;

  // Same gating Product.js uses — restaurants below "basic" can't take
  // online orders at all, and a restaurant currently outside its posted
  // opening hours can't take orders either (server-side enforced too,
  // see addOrderItems).
  const canOrder =
    ["basic", "pro"].includes(
      product?.subscription_tier?.toLowerCase()
    ) && !isClosed;

  /* Re-fetch whenever the selected language changes so the translated
     name/description/options are loaded. */
  useEffect(() => {
    dispatch(listProductDetails(id));
  }, [dispatch, id, language]);

  const close = () => navigate(-1);

  /* Locks the background from scrolling while the sheet is open (the
     menu list stays mounted underneath — see App.js — so it must stay
     exactly where the user left it, not scroll along with the sheet).

     Plain `overflow: hidden` on the body isn't enough — most browsers
     snap window.scrollY to 0 the instant the body stops being
     scrollable, losing the position instead of just freezing it. The
     standard workaround: pin the body at its current scroll offset via
     position: fixed + a negative top, then undo it and restore
     window.scrollTo on close.

     The restore also has to fight the site's global `html { scroll-
     behavior: smooth }` (LandingScreen.css) — without overriding it,
     the browser animates from the (visually reset-to-0) body back up
     to scrollY instead of snapping straight there, which read as the
     page jumping to the top and then scrolling back down. */
  useEffect(() => {
    if (!isOverlay) return;

    const scrollY = window.scrollY;
    const { body, documentElement: html } = document;
    body.classList.add("sheet-open");
    body.style.top = `-${scrollY}px`;

    return () => {
      body.classList.remove("sheet-open");
      body.style.top = "";

      const prevScrollBehavior = html.style.scrollBehavior;
      html.style.scrollBehavior = "auto";
      window.scrollTo(0, scrollY);
      html.style.scrollBehavior = prevScrollBehavior;
    };
  }, [isOverlay]);

  /* Esc closes the sheet, same as the backdrop/close button/swipe. */
  useEffect(() => {
    if (!isOverlay) return;
    const onKeyDown = (e) => {
      if (e.key === "Escape") close();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOverlay]);

  /* ===== SWIPE-DOWN-TO-CLOSE (mobile sheet handle) ===== */
  const [dragY, setDragY] = useState(0);
  const [dragging, setDragging] = useState(false);
  const dragStartY = useRef(0);

  const handleDragStart = (e) => {
    dragStartY.current = e.touches[0].clientY;
    setDragging(true);
  };

  const handleDragMove = (e) => {
    if (!dragging) return;
    const delta = e.touches[0].clientY - dragStartY.current;
    if (delta > 0) setDragY(delta);
  };

  const handleDragEnd = () => {
    setDragging(false);
    if (dragY > SWIPE_CLOSE_THRESHOLD) {
      close();
      return;
    }
    setDragY(0);
  };

  /* ===== QTY ===== */
  const increment = () => setQuantity((prev) => prev + 1);
  const decrement = () => setQuantity((prev) => (prev > 1 ? prev - 1 : 1));

  /* ===== OPTION LOGIC ===== */
  const toggleOption = (group, option) => {
    setSelectedOptions((prev) => {
      const current = prev[group.id] || [];

      // Radio (max 1)
      if (group.max_choices === 1) {
        return {
          ...prev,
          [group.id]: [option.id],
        };
      }

      // Checkbox
      if (current.includes(option.id)) {
        return {
          ...prev,
          [group.id]: current.filter((id) => id !== option.id),
        };
      }

      if (group.max_choices && current.length >= group.max_choices) {
        return prev;
      }

      return {
        ...prev,
        [group.id]: [...current, option.id],
      };
    });
  };

  /* ===== PRICE CALC ===== */
  const selectedOptionObjects =
    product?.option_groups?.flatMap((group) =>
      group.options.filter((opt) =>
        selectedOptions[group.id]?.includes(opt.id),
      ),
    ) || [];

  const extraTotal = selectedOptionObjects.reduce(
    (sum, opt) => sum + Number(opt.extra_price || 0),
    0,
  );

  const basePrice = Number(product?.price || 0);
  const finalPrice = basePrice + extraTotal;

  /* ===== VALIDATION ===== */
  const optionsValid =
    product?.option_groups?.every((group) => {
      if (!group.required) return true;
      return (selectedOptions[group.id] || []).length > 0;
    }) ?? true;

  /* ===== ADD TO CART ===== */
  const addToCartHandler = () => {
    if (!optionsValid) {
      setShowErrors(true);
      return;
    }

    dispatch(
      addToCart({
        product: product._id,
        name: product.name,
        qty,
        price: finalPrice,
        image: product.image,
        selectedOptions: selectedOptionObjects.map((opt) => ({
          optionId: opt.id,
          name: opt.name,
          extra_price: opt.extra_price,
        })),
      }),
    );

    close();
  };

  const sheetStatus = (content) => (
    <div className="product-sheet-overlay" onClick={close} role="presentation">
      <div className="product-sheet" onClick={(e) => e.stopPropagation()}>
        <button
          type="button"
          className="product-sheet-close"
          onClick={close}
          aria-label={t("closeAria", language)}
        >
          <FaTimes />
        </button>
        <div className="product-sheet-scroll">{content}</div>
      </div>
    </div>
  );

  /* ===== STATES ===== */
  if (loading) {
    const skeleton = <ProductDetailSkeleton />;
    return isOverlay ? (
      sheetStatus(skeleton)
    ) : (
      <div className="home-container product-detail-container">{skeleton}</div>
    );
  }

  if (error) {
    const errorMessage = <Message variant="danger">{error}</Message>;
    return isOverlay ? (
      sheetStatus(errorMessage)
    ) : (
      <div className="home-container">{errorMessage}</div>
    );
  }

  if (!product) {
    const noData = <Message variant="info">{t("noProductData", language)}</Message>;
    return isOverlay ? (
      sheetStatus(noData)
    ) : (
      <div className="home-container">{noData}</div>
    );
  }

  /* Product descriptions store the allergen note inline as free text
     ("... Allergének: glutén, tojás, ..."); split it off purely for
     display so it reads as a distinct, de-emphasized note. Machine
     translation renders the label itself in whichever language it
     translated to, so try every known variant regardless of the
     currently selected language. */
  const allergenLabelPattern = ALLERGEN_LABELS.map(
    (label) => label.replace(":", ""),
  ).join("|");
  const allergenSplit = product.description?.match(
    new RegExp(`^([\\s\\S]*?)\\s*(${allergenLabelPattern}):\\s*([\\s\\S]*)$`, "i"),
  );
  const mainDescription = allergenSplit ? allergenSplit[1].trim() : product.description;
  const allergenLabel = allergenSplit ? allergenSplit[2] : null;
  const allergenText = allergenSplit ? allergenSplit[3].trim() : null;

  const scrollableContent = (
    <>
      <div className="product-image-wrap">
        <ProductImage
          src={product.image}
          alt={product.name}
          className="product-image"
        />
        {!isOverlay && (
          <button
            type="button"
            className="product-back-btn"
            onClick={close}
            aria-label={t("backAria", language)}
          >
            <FaChevronLeft />
          </button>
        )}
      </div>

      <h2 className="product-name">{product.name}</h2>
      {!hidePrices && (
        <div className="product-price">{product.price} Ft</div>
      )}

      {mainDescription && (
        <p className="product-description">{mainDescription}</p>
      )}

      {allergenText && (
        <p className="product-allergens">
          {allergenLabel}: {allergenText}
        </p>
      )}

      {/* ===== OPTIONS ===== */}
      {product.option_groups?.length > 0 && (
        <div className="product-options">
          {product.option_groups.map((group) => {
            const hasError =
              showErrors &&
              group.required &&
              !(selectedOptions[group.id]?.length > 0);

            return (
              <div
                key={group.id}
                className={`option-group ${hasError ? "option-group-error" : ""}`}
              >
                <div className="option-group-title">
                  {group.name}
                  {group.required && (
                    <span className="option-group-required">
                      {t("required", language)}
                    </span>
                  )}
                </div>

                {group.options.map((opt) => {
                  const checked =
                    selectedOptions[group.id]?.includes(opt.id) || false;

                  return (
                    <label
                      key={opt.id}
                      className={`option-row custom-option ${
                        checked ? "selected" : ""
                      }`}
                    >
                      <input
                        type={group.max_choices === 1 ? "radio" : "checkbox"}
                        name={`group-${group.id}`}
                        checked={checked}
                        onChange={() => toggleOption(group, opt)}
                      />

                      <span className="custom-check" />

                      <span className="option-label">{opt.name}</span>

                      {!hidePrices && opt.extra_price > 0 && (
                        <span className="extra">+{opt.extra_price} Ft</span>
                      )}
                    </label>
                  );
                })}

                {hasError && (
                  <div className="option-error-text">
                    {t("selectAtLeastOne", language)}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {canOrder && (
        <UpsellModule context="item_detail" sourceProduct={product._id} />
      )}
    </>
  );

  const actionRow = canOrder && (
    <div className="product-action-row">
      <div className="quantity-box">
        <button onClick={decrement} className="qty-button">
          –
        </button>

        <span className="qty-value">{qty}</span>

        <button onClick={increment} className="qty-button">
          +
        </button>
      </div>

      <div
        className={`add-to-cart-box ${
          !optionsValid ? "disabled" : ""
        }`}
        onClick={addToCartHandler}
      >
        <span className="add-to-cart-label">
          <FaShoppingCart />
          {t("addToCart", language)}
        </span>
        {!hidePrices && <span>{qty * finalPrice} Ft</span>}
      </div>
    </div>
  );

  if (!isOverlay) {
    return (
      <div
        className={`home-container product-detail-container ${
          canOrder ? "has-sticky-action" : ""
        }`}
      >
        {scrollableContent}
        {actionRow}
      </div>
    );
  }

  return (
    <div className="product-sheet-overlay" onClick={close} role="presentation">
      <div
        className="product-sheet"
        role="dialog"
        aria-modal="true"
        aria-label={product.name}
        onClick={(e) => e.stopPropagation()}
        style={{
          transform: dragY ? `translateY(${dragY}px)` : undefined,
          transition: dragging ? "none" : undefined,
        }}
      >
        <div
          className="product-sheet-handle"
          onTouchStart={handleDragStart}
          onTouchMove={handleDragMove}
          onTouchEnd={handleDragEnd}
        >
          <span className="product-sheet-handle-bar" />
        </div>

        <button
          type="button"
          className="product-sheet-close"
          onClick={close}
          aria-label={t("closeAria", language)}
        >
          <FaTimes />
        </button>

        <div className="product-sheet-scroll">{scrollableContent}</div>

        {actionRow}
      </div>
    </div>
  );
}

export default ProductDetailScreen;
